/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { get, set, action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import config from 'linced-app/config/environment';
import moment from 'moment';
import { formatCurrency } from 'linced-app/helpers/format-currency';
import { getErrorMessages } from 'linced-app/utils/xero-errors';
import {
  filterBy,
  findBy,
  mapBy,
  rejectBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';

function getLedgerName(transaction) {
  let ledger = transaction.get('lineItem.ledger');
  let ledgerName = '';
  if (ledger) {
    ledgerName = `${ledger.get('code')} - ${ledger.get('name')}`;
  }
  return ledgerName;
}

export default class TransactionsController extends Controller {
  // Services
  @service
  fetch;

  @service
  options;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  TransactionType = TransactionType;

  @tracked
  sortDir = 'asc';

  @tracked
  sortProp = 'date';

  @tracked
  from = '';

  @tracked
  to = '';

  @tracked
  transactionType = null;

  @tracked
  cancelled = false;

  @computed('filteredData.@each.{isCancelled,amount}')
  get ledgerTotal() {
    return this.filteredData.reduce((total, transaction) => {
      if (transaction.isCancelled) {
        return total;
      }
      return total + transaction.amount;
    }, 0);
  }

  @computed('filteredData.@each.{isCancelled,tax}')
  get taxTotal() {
    return this.filteredData.reduce((total, transaction) => {
      if (transaction.isCancelled) {
        return total;
      }
      return total + transaction.tax;
    }, 0);
  }

  @tracked
  filters = [];

  @optionAlias('finance:xero:enableXeroIntegration')
  xeroIntegrationEnabled;

  @optionAlias('finance:xero:heroJournalPostingType')
  heroJournalPostingType;

  @computed('model.piaLedgerItem.ledger.{code,name}')
  get piaLedgerLabel() {
    let ledger = this.get('model.piaLedgerItem.ledger');
    if (isEmpty(ledger)) {
      return '';
    }
    return `${ledger.get('code')} - ${ledger.get('name')}`;
  }

  @computed('transactionType', 'xeroIntegrationEnabled')
  get sendXeroTransferBatch() {
    if (!this.xeroIntegrationEnabled) {
      return false;
    }
    return parseInt(this.transactionType, 10) === TransactionType.TRANSFER;
  }

  get strikeCancelled() {
    // Cancelled transactions should only be struck out on the
    // "All Transactions" report.
    return this.transactionType === '0';
  }

  get hidePayer() {
    return parseInt(this.transactionType, 10) === TransactionType.ADJUSTMENT;
  }

  // data is an array of table-ready data rows, to ease the sorting and filtering process.
  @computed(
    'model.{transactions.[],apportionments.[]}',
    'model.transactions.@each.{authorName,paidBy}',
    'piaLedgerLabel',
    'transactionType',
  )
  get data() {
    return this.model.transactions.reduce((rows, transaction) => {
      if (transaction.type === TransactionType.PAYMENT) {
        // Payment records may have apportionments (child transactions), which should be displayed in the table.
        transaction.childTransactions.forEach((txn) => {
          let isPIA = !txn.get('lineItem.id');
          let ledger = isPIA ? this.piaLedgerLabel : getLedgerName(txn);
          rows.push({
            id: txn.id,
            ledger,
            date: transaction.date,
            item: isPIA ? { name: 'Payment in Advance' } : txn.lineItem,
            comment: transaction.comment,
            account: txn.account,
            displayMethod: txn.displayMethod,
            amount: isPIA ? txn.amount : txn.displayAmount,
            tax: txn.taxAmount,
            lineItemId: txn.get('lineItem.id'),
            paidBy: transaction.paidBy,
            author: transaction.authorName,
            isCancelled: transaction.isCancelled,
            cancellationReason: transaction.cancellationReason,
          });
        });

        if (transaction.piaAmount > 0) {
          rows.push({
            id: transaction.id,
            ledger: this.piaLedgerLabel,
            date: transaction.date,
            item: { name: 'Payment in Advance' },
            comment: transaction.comment,
            account: transaction.account,
            displayMethod: transaction.displayMethod,
            amount: transaction.piaAmount,
            tax: 0,
            paidBy: transaction.paidBy,
            author: transaction.authorName,
            isCancelled: transaction.isCancelled,
            cancellationReason: transaction.cancellationReason,
          });
        }
      } else if (!(
        this.transactionType === '0' &&
        [
          TransactionType.APPORTIONMENT,
          TransactionType.ADJUSTMENT, // Don't show adjustments in All Transactions
        ].includes(transaction.type)
      )) {
        // Other transactions are self-contained; just add them to the list.
        let isAdjustment = transaction.type === TransactionType.ADJUSTMENT;
        rows.push({
          id:
            transaction.type === TransactionType.APPORTIONMENT
              ? ''
              : parseInt(transaction.id, 10),
          ledger: getLedgerName(transaction),
          date: transaction.date,
          item: transaction.lineItem,
          comment: transaction.comment,
          account: transaction.account,
          displayMethod: transaction.displayMethod,
          amount: isAdjustment ? transaction.amount : transaction.displayAmount,
          tax: isAdjustment
            ? transaction.taxAmount * -1
            : transaction.taxAmount,
          paidBy: transaction.paidBy,
          author: transaction.authorName,
          lineItemId:
            transaction.type === TransactionType.APPORTIONMENT
              ? ''
              : transaction.get('lineItem.id'),
          xeroBatchNo: transaction.xeroBatchNo,
          isPosted: transaction.isPosted,
          isXeroLedger:
            transaction.type === TransactionType.APPORTIONMENT
              ? false
              : transaction.get('lineItem.ledger.taxType'),
          isCancelled: transaction.isCancelled,
          cancellationReason: transaction.cancellationReason,
        });
      }

      return rows;
    }, []);
  }

  // filteredData applies the user's filters to the data array computed above.
  @computed('data.[]', 'filters.@each.{field,value}')
  get filteredData() {
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.data;
    }

    // Map raw filters into what we need to actually perform the filtering.
    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      let key = filter.field;
      switch (filter.field) {
        case 'item':
          key = 'item.name';
          break;
        case 'description':
          key = 'item.description';
          break;
        case 'jurisdictionalId':
        case 'firstName':
        case 'lastName':
          key = `account.person.${filter.field}`;
          break;
        case 'amount':
        case 'tax':
          dataType = DataType.FLOAT;
          break;
        case 'xeroBatchNo':
          dataType = DataType.INTEGER;
          break;
      }

      return {
        key,
        dataType,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a (potentially) large dataset multiple times.
    return this.data.filter((row) => {
      return filters.every((filter) => {
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @computed('filteredData.[]', 'sortProp', 'sortDir', 'sendXeroTransferBatch')
  get exportData() {
    let data = sortBy(this.filteredData, this.sortProp);
    if (this.sortDir === 'desc') {
      data = data.reverse();
    }
    return data.map((row) => {
      let cancellationReason = row.isCancelled
        ? ' Cancelled: ' + row.cancellationReason
        : '';
      let exportRow = [
        row.id || '',
        row.ledger || '',
        row.date || '',
        row.author || '',
        get(row, 'item.name') || '',
        get(row, 'item.description') || '',
        `${row.comment || ''}${cancellationReason}`,
        row.lineItemId || '',
        get(row, 'account.person.jurisdictionalId') || '',
        get(row, 'account.person.lastName') || '',
        get(row, 'account.person.firstName') || '',
        row.displayMethod || '',
        row.paidBy || '',
        formatCurrency([row.amount], { currency: '$' }),
        formatCurrency([row.tax], { currency: '$' }),
      ];

      if (this.sendXeroTransferBatch) {
        exportRow.unshift('');
        exportRow.splice(14, 0, row.xeroBatchNo || '');
      }

      return exportRow;
    });
  }

  @tracked
  selectedTransactions = [];

  @tracked
  _reportTitle = null;

  @action
  set_reportTitle(value) {
    this._reportTitle = value;
  }

  get reportTitle() {
    if (this._reportTitle !== null) {
      return this._reportTitle;
    }
    let typeName = TransactionType.getLabel(parseInt(this.transactionType, 10));
    if (this.cancelled) {
      typeName = `Cancelled ${typeName}`;
    }
    if (isEmpty(typeName)) {
      typeName = 'All Transactions';
      if (this.cancelled) {
        typeName = 'All Cancelled Transactions';
      }
    }
    return `Transaction List: ${typeName} (${this.from} - ${this.to})`;
  }

  allSelected = false;

  @tracked
  errorDialogOpen = false;

  @tracked
  errorMessages = [];

  @tracked
  errorHeader = 'Failed to create a Batch';

  @tracked
  creatingBatch;

  @tracked
  markingBatchPosted;

  @tracked
  methodsWithAllSelected;

  @action
  sortChanged({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  @action
  toggleSelectAll() {
    this.toggleProperty('allSelected');
    if (this.allSelected) {
      this.selectedTransactions = mapBy(
        filterBy(rejectBy(this.filteredData, 'isPosted'), 'isXeroLedger'),
        'id',
      );
    } else {
      this.selectedTransactions = [];
    }
  }

  @action
  markAsPosted() {
    if (isEmpty(this.selectedTransactions)) {
      this.toaster.error('Please select transactions to proceed.');
      return;
    }
    this.markingBatchPosted = true;
    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/transactions/:bulkUpdateTxnXeroBatch`;
    let data = {
      schoolId: this.session.activeSchool,
      transactionIds: this.selectedTransactions,
      xeroBatchNo: 0,
      isPosted: true,
    };
    return this.fetch
      .post(url, { data })
      .then(() => {
        this.selectedTransactions = [];
        this.methodsWithAllSelected = [];
        this.markingBatchPosted = false;
        this.router.refresh('admin.reports.finance.transactions');
        this.toaster.success(
          'Transactions has been marked as posted successfully!',
        );
      })
      .catch(() => {
        this.markingBatchPosted = false;
        this.toaster.error(
          'Error in marking transactions as posted. Please try again.',
        );
      });
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  toggleBatch(transactionId) {
    if (this.selectedTransactions.includes(transactionId)) {
      this.selectedTransactions = this.selectedTransactions.filter(
        (id) => id !== transactionId,
      );
    } else {
      this.selectedTransactions = [...this.selectedTransactions, transactionId];
    }
  }

  @action
  closeErrorDialog() {
    this.errorDialogOpen = false;
  }

  @action
  sendBatchToXero() {
    // heroClearingAccountCode is used to create a debit line for balancing journals sent to XERO.
    // Since transfers are made from Payment in Advance Ledger,
    // for sending transfer journals PIA Ledger code is used as heroClearingAccountCode.
    let heroClearingAccountCode = this.options.value(
      'finance:xero:piaLedgerCode',
    );
    if (
      !isEmpty(this.selectedTransactions) &&
      !isEmpty(heroClearingAccountCode)
    ) {
      this.creatingBatch = true;
      this.store
        .createRecord('xero/batch', {
          schoolId: this.session.school,
          transactionIds: this.selectedTransactions,
          heroClearingAccountCode,
          narration: `Hero TRANSFER Batch %d - Sent ${moment().format(
            'DD/MM/YYYY',
          )}`,
          journalDate: moment(this.journalDate).format('YYYY-MM-DD'),
          batchStatus: this.heroJournalPostingType,
        })
        .save()
        .then((batchResponse) => {
          if (!isEmpty(batchResponse.warningMessages)) {
            this.errorMessages = batchResponse.warningMessages;
            this.errorHeader = `Batch Number ${batchResponse.batchNo} created with warnings`;
            this.errorDialogOpen = true;
          } else {
            this.toaster.success(
              `Batch Number ${batchResponse.batchNo} sent successfully!`,
            );
          }
          this.selectedTransactions = [];
          this.methodsWithAllSelected = [];
          this.router.refresh('admin.reports.finance.transactions');
        })
        .catch((err) => {
          this.errorMessages = getErrorMessages(err);
          this.errorHeader = 'Failed to create Batch';
          this.errorDialogOpen = true;
        })
        .finally(() => {
          this.creatingBatch = false;
        });
    } else {
      let errorMessage = 'Failed to create Batch.';
      if (isEmpty(this.selectedTransactions)) {
        errorMessage += ' Please select transactions to proceed.';
      }
      if (isEmpty(heroClearingAccountCode)) {
        errorMessage +=
          ' Please add "Payment in Advance Ledger Code" under Finance Options.';
      }
      this.toaster.error(errorMessage);
    }
  }

  reset() {
    this.filters = [];
    this.selectedTransactions = [];
  }
}
